import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "container", "results", "chips"]
  static values = {
    url: String,
    announcementId: String
  }

  connect() {
    this.timeout = null
    this.selectedUsers = []
    this.loadExistingChips()
    document.addEventListener("click", this.handleClickOutside.bind(this))
  }

  loadExistingChips() {
    const existingChips = this.chipsTarget.querySelectorAll('.sc-recipient-chip')
    existingChips.forEach(chip => {
      const hiddenInput = chip.querySelector('input[type="hidden"]')
      const nameEl = chip.querySelector('.sc-recipient-chip-name')
      if (hiddenInput && nameEl) {
        this.selectedUsers.push({
          id: hiddenInput.value,
          name: nameEl.textContent.trim()
        })
      }
    })
  }

  focusInput() {
    this.inputTarget.focus()
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
  }

  search() {
    clearTimeout(this.timeout)
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.timeout = setTimeout(() => {
      this.fetchResults(query)
    }, 250)
  }

  async fetchResults(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('q', query)
  
    if (this.announcementIdValue) {
      url.searchParams.set('announcement_id', this.announcementIdValue)
    }
  
    const excludeIds = this.selectedUsers.map(u => u.id).join(',')
    if (excludeIds) {
      url.searchParams.set('exclude', excludeIds)
    }
  
    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (!response.ok) throw new Error("Server error")
  
      const users = await response.json()
      this.renderResults(users)
    } catch (error) {
      console.error("Error fetching recipients:", error)
    }
  }

  renderResults(users) {
    const selectedIds = this.selectedUsers.map(u => u.id)
    const filteredUsers = users.filter(u => !selectedIds.includes(u.id))

    if (filteredUsers.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="sc-padding-small uk-text-center uk-text-muted uk-text-small">
          ${this.inputTarget.value.length >= 2 ? 'Nie znaleziono użytkowników' : ''}
        </div>
      `
      this.showResults()
      return
    }

    this.resultsTarget.innerHTML = filteredUsers.map(user => {
      const avatarUrl = user.avatar_url
      const initials = user.initials || '?'
      const initialsColor = this.getInitialsColor(initials)
      
      const avatarHtml = (avatarUrl && !avatarUrl.includes('defaults/avatar.png')) 
        ? `<img src="${avatarUrl}" alt="${user.name}">`
        : `<span class="sc-avatar-initials" style="background: ${initialsColor};">${initials}</span>`
      
      return `
        <div class="sc-autocomplete-item" data-action="click->recipient-autocomplete#select" data-user='${JSON.stringify(user).replace(/'/g, "&#39;")}'>
          <div class="sc-autocomplete-info">
            <div class="sc-autocomplete-avatar">
              ${avatarHtml}
            </div>
            <div class="sc-autocomplete-details">
              <span class="sc-autocomplete-name">${user.name}</span>
              <span class="sc-autocomplete-email">${user.email}</span>
            </div>
          </div>
        </div>
      `
    }).join("")

    this.showResults()
  }

  getInitialsColor(initials) {
    if (!initials || initials.length === 0) {
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }

    const firstChar = initials.charAt(0).toUpperCase()
    const charCode = firstChar.charCodeAt(0)
    
    const gradients = [
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // A-B
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // C-D
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // E-F
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // G-H
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // I-J
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // K-L
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // M-N
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // O-P
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Q-R
      'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', // S-T
      'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)', // U-V
      'linear-gradient(135deg, #c471ed 0%, #f64f59 100%)', // W-X
      'linear-gradient(135deg, #12c2e9 0%, #c471ed 100%)', // Y-Z
    ]

    const index = Math.floor((charCode - 65) / 2) % gradients.length
    return gradients[index] || gradients[0]
  }

  select(event) {
    const userData = event.currentTarget.dataset.user
    const user = JSON.parse(userData)
    
    // Add to selected users
    if (!this.selectedUsers.find(u => u.id === user.id)) {
      this.selectedUsers.push(user)
      this.renderChips()
    }
    
    // Clear input and hide results
    this.inputTarget.value = ""
    this.hideResults()
    this.inputTarget.focus()
  }

  renderChips() {
    this.chipsTarget.innerHTML = this.selectedUsers.map(user => `
      <span class="sc-recipient-chip" data-user-id="${user.id}">
        <span class="sc-recipient-chip-name">${user.name}</span>
        <a href="#" class="sc-recipient-chip-remove" data-action="click->recipient-autocomplete#remove" data-user-id="${user.id}">
          <i class="mdi mdi-close"></i>
        </a>
        <input type="hidden" name="recipient_ids[]" value="${user.id}" />
      </span>
    `).join("")
  }

  remove(event) {
    event.preventDefault()
    event.stopPropagation()
    const userId = event.currentTarget.dataset.userId
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId)
    this.renderChips()
    this.inputTarget.focus()
  }

  showResults() {
    this.resultsTarget.classList.remove("uk-hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("uk-hidden")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  inputFocus() {
    if (this.inputTarget.value.length >= 2) {
      this.search()
    }
  }

  // Handle backspace to remove last chip
  handleKeydown(event) {
    if (event.key === "Backspace" && this.inputTarget.value === "" && this.selectedUsers.length > 0) {
      this.selectedUsers.pop()
      this.renderChips()
    }
  }
}
